import type { TableStatus } from "./tableTones";

// Под 8px надписът под номера вече не се чете.
export const MIN_BADGE_SIZE = 8;
// Иконата (1em) + разстоянието до текста (0.25em).
const ICON_EM = 1.25;
// Ширината на удебелен символ в em, когато няма canvas (тестове).
const FALLBACK_CHAR_EM = 0.62;

let measureContext: CanvasRenderingContext2D | null | undefined;
const widthCache = new Map<string, number>();

/**
 * Ширината на текста в em (удебелен, шрифта на страницата), с кеш. Мери се
 * на 8px: системният шрифт е по-широк на дребни размери (оптичен размер), а
 * на по-големи е малко по-тесен — затова оценката е безопасна и нагоре.
 */
export const textWidthEm = (text: string): number => {
  const cached = widthCache.get(text);
  if (cached !== undefined) return cached;
  if (measureContext === undefined) {
    const isBrowser = typeof document !== "undefined" && !navigator.userAgent.includes("jsdom");
    measureContext = isBrowser ? document.createElement("canvas").getContext("2d") : null;
    if (measureContext) {
      measureContext.font = `700 ${MIN_BADGE_SIZE}px ${getComputedStyle(document.body).fontFamily}`;
    }
  }
  const width = measureContext
    ? measureContext.measureText(text).width / MIN_BADGE_SIZE
    : text.length * FALLBACK_CHAR_EM;
  widthCache.set(text, width);
  return width;
};

interface BadgeLayout {
  text: string;
  showIcon: boolean;
  size: number;
}

/**
 * Надписът под номера се свива до ширината на плота (`room` — реалното място
 * за реда, без отстъпите). Не се ли събира и на 8px, опитва първо без
 * иконата, после сгъстения вариант („4:52 ч" / само ✓). Не се ли събира
 * нищо — редът се скрива (плотът показва статуса с тона си, а подробностите
 * са в подсказката), вместо да излезе отрязан „2 м…".
 */
export const fitBadge = (
  badge: TableStatus["badge"],
  hasIcon: boolean,
  { room, maxSize }: { room: number; maxSize: number },
): BadgeLayout | null => {
  if (!badge) return null;
  // Най-големият размер, при който редът се събира в `room`.
  const fitSizeFor = (text: string, withIcon: boolean) =>
    room / Math.max(0.01, textWidthEm(text) + (withIcon ? ICON_EM : 0));
  const candidates = [
    { text: badge.text, showIcon: hasIcon },
    { text: badge.text, showIcon: false },
    ...(badge.compactText !== undefined
      ? [
          { text: badge.compactText, showIcon: hasIcon },
          { text: badge.compactText, showIcon: false },
        ]
      : []),
  ].filter((candidate) => candidate.text !== "" || candidate.showIcon);
  const fitting = candidates.find(
    (candidate) => fitSizeFor(candidate.text, candidate.showIcon) >= MIN_BADGE_SIZE,
  );
  if (!fitting) return null;
  // До дизайнерския размер (`maxSize`), но никога под 8px — при дребен мащаб
  // дизайнерският е под 8px, а редът пак се събира на 8px.
  const size = Math.max(MIN_BADGE_SIZE, Math.min(maxSize, fitSizeFor(fitting.text, fitting.showIcon)));
  return { ...fitting, size };
};
